/**
 * useDomainDashboard Hook
 * React hook for subscribing to real-time domain dashboard metrics
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { useCCMS } from '../../../react/hooks/useCCMS';
import { DomainDashboardClient } from '../DomainDashboardClient';
import { ConnectionState } from '../../shared/types';
/**
 * Hook for real-time domain dashboard metrics
 * @param domain - Dashboard domain (e.g., 'taskmanagement', 'projects')
 * @param options - Configuration options
 */
export function useDomainDashboard(domain, options = {}) {
    const { config } = useCCMS();
    const [metrics, setMetrics] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [error, setError] = useState(null);
    const clientRef = useRef(null);
    const { autoConnect = false, autoSubscribe = true, filter = {}, refreshInterval = 30, onMetrics, onBadgeSnapshot, onBadgeDelta, onActivityDelta, onPresenceSnapshot, onPresenceJoined, onPresenceLeft, } = options;
    // Stash the callback refs so the connect closure stays stable while the
    // caller's handlers can change between renders.
    const onBadgeSnapshotRef = useRef(onBadgeSnapshot);
    const onBadgeDeltaRef = useRef(onBadgeDelta);
    const onActivityDeltaRef = useRef(onActivityDelta);
    const onPresenceSnapshotRef = useRef(onPresenceSnapshot);
    const onPresenceJoinedRef = useRef(onPresenceJoined);
    const onPresenceLeftRef = useRef(onPresenceLeft);
    useEffect(() => { onBadgeSnapshotRef.current = onBadgeSnapshot; }, [onBadgeSnapshot]);
    useEffect(() => { onBadgeDeltaRef.current = onBadgeDelta; }, [onBadgeDelta]);
    useEffect(() => { onActivityDeltaRef.current = onActivityDelta; }, [onActivityDelta]);
    useEffect(() => { onPresenceSnapshotRef.current = onPresenceSnapshot; }, [onPresenceSnapshot]);
    useEffect(() => { onPresenceJoinedRef.current = onPresenceJoined; }, [onPresenceJoined]);
    useEffect(() => { onPresenceLeftRef.current = onPresenceLeft; }, [onPresenceLeft]);
    /**
     * Connect to the domain dashboard hub
     */
    const connect = useCallback(async () => {
        if (clientRef.current?.isConnected()) {
            return;
        }
        try {
            if (!clientRef.current) {
                clientRef.current = new DomainDashboardClient(config.dashboardsUrl || config.baseUrl, domain, async () => {
                    if (config.getToken) {
                        return await config.getToken();
                    }
                    return null;
                });
                clientRef.current.onStateChange((state, err) => {
                    setIsConnected(state === ConnectionState.Connected);
                    if (err) {
                        setError(err);
                    }
                });
                // Subscribe to metrics events
                clientRef.current.on('metrics', (data) => {
                    setMetrics(data);
                    onMetrics?.(data);
                });
                clientRef.current.on('unsubscribed', () => {
                    setIsSubscribed(false);
                });
                // Badge events (Phase 3+). Snapshots arrive on connect / reconnect;
                // deltas stream from RabbitMQ consumers as domain events fire.
                clientRef.current.on('badgeSnapshot', (snapshot) => {
                    onBadgeSnapshotRef.current?.(snapshot);
                });
                clientRef.current.on('badgeDelta', (delta) => {
                    onBadgeDeltaRef.current?.(delta);
                });
                // TaskManagement activity drawer: live rows plus module presence.
                clientRef.current.on('activityDelta', (delta) => {
                    onActivityDeltaRef.current?.(delta);
                });
                clientRef.current.on('presenceSnapshot', (message) => {
                    onPresenceSnapshotRef.current?.(message);
                });
                clientRef.current.on('presenceJoined', (message) => {
                    onPresenceJoinedRef.current?.(message);
                });
                clientRef.current.on('presenceLeft', (message) => {
                    onPresenceLeftRef.current?.(message);
                });
            }
            await clientRef.current.connect();
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, [config, domain, onMetrics]);
    /**
     * Disconnect from the hub
     */
    const disconnect = useCallback(async () => {
        // Null the ref SYNCHRONOUSLY before awaiting the teardown. Under React StrictMode the
        // mount→unmount→remount cycle calls this cleanup between the two mounts; if the ref were only
        // nulled after `await client.disconnect()`, the remount's connect() would see the still-present
        // dying client and reuse it (and fail). Detaching first lets the remount build a fresh client.
        const client = clientRef.current;
        clientRef.current = null;
        setIsConnected(false);
        setIsSubscribed(false);
        setMetrics(null);
        setError(null);
        if (client) {
            try {
                await client.disconnect();
            }
            catch { /* teardown of a detached client is best-effort */ }
        }
    }, []);
    /**
     * Subscribe to dashboard metrics with optional filter
     */
    const subscribe = useCallback(async (customFilter) => {
        if (!clientRef.current?.isConnected()) {
            await connect();
        }
        try {
            await clientRef.current.subscribe(customFilter || filter, refreshInterval);
            setIsSubscribed(true);
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, [connect, filter, refreshInterval]);
    /**
     * Unsubscribe from dashboard metrics
     */
    const unsubscribe = useCallback(async () => {
        if (clientRef.current && isSubscribed) {
            try {
                await clientRef.current.unsubscribe();
                setIsSubscribed(false);
            }
            catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                setError(error);
                throw error;
            }
        }
    }, [isSubscribed]);
    /**
     * Request immediate metrics refresh
     */
    const refresh = useCallback(async () => {
        if (!clientRef.current?.isConnected()) {
            throw new Error('Not connected to dashboard hub');
        }
        try {
            await clientRef.current.refresh();
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, []);
    /**
     * Get chart data by method name
     */
    const getChart = useCallback(async (methodName) => {
        if (!clientRef.current?.isConnected()) {
            throw new Error('Not connected to dashboard hub');
        }
        try {
            return await clientRef.current.getChart(methodName);
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, []);
    /**
     * Auto-connect and auto-subscribe on mount if enabled
     */
    useEffect(() => {
        if (autoConnect) {
            connect()
                .then(() => {
                if (autoSubscribe) {
                    return subscribe();
                }
            })
                .catch((err) => {
                console.error(`[useDomainDashboard:${domain}] Auto-connect failed:`, err);
            });
        }
        return () => {
            disconnect();
        };
    }, [autoConnect]);
    /**
     * Request a fresh badge snapshot from the server. Useful from a reconnect
     * handler — the SDK's automatic reconnect emits state changes but won't
     * re-invoke server methods on its own.
     */
    const requestBadgeSnapshot = useCallback(async () => {
        if (clientRef.current?.isConnected()) {
            await clientRef.current.requestBadgeSnapshot();
        }
    }, []);
    /**
     * Ask for the current presence roster. Shares the hub's RequestBadgeSnapshot
     * entry point, which also re-joins the presence group after a reconnect.
     */
    const requestPresenceSnapshot = useCallback(async () => {
        if (clientRef.current?.isConnected()) {
            await clientRef.current.requestPresenceSnapshot();
        }
    }, []);
    /** The user IDs currently viewing the module, fetched on demand. */
    const getPresentUsers = useCallback(async () => {
        if (!clientRef.current?.isConnected())
            return [];
        return clientRef.current.getPresentUsers();
    }, []);
    return {
        metrics,
        isConnected,
        isSubscribed,
        error,
        connect,
        disconnect,
        subscribe,
        unsubscribe,
        refresh,
        getChart,
        requestBadgeSnapshot,
        requestPresenceSnapshot,
        getPresentUsers,
    };
}
//# sourceMappingURL=useDomainDashboard.js.map